import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import { EntityPropertyTypes } from '../../../../../enums/entityPropertyTypes';
import { FetchEntitiesFilters } from '../../../../../enums/fetchEntitiesFilters';
import { enumTypes, getEnumValue } from '../../../../../utils/enumHelper';
import EntitiesMultiSelect from '../../../../Common/Controls/EntitiesMultiSelect/entitiesMultiSelect';
import * as dialogHelper from 'infrastructure/js/components/Dialog/dialogHelper';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox.js';
import Checkbox from 'infrastructure/js/components/controls/Checkbox/checkbox';
import MessageDialog from 'infrastructure/js/components/Dialog/MessageDialog/messageDialog';
import AddRemoveListExt from '../../../../Common/Controls/AddRemoveListExt/addRemoveListExt';
import SensorsListItem from './SensorsListItem/sensorsListItem';
import PermissionManager from 'infrastructure/js/utils/permissionManager';

import './shipToCustomerDialog.scss';

class ShipToCustomerDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.dialogLabels = createLabelHelper('mat.dialog.');
    this.sensorIntegrationErrorLabels = createLabelHelper('mat.locationpage.view.shipments.sensorIntegrationError.dialog.');
    this.labels = createLabelHelper('mat.locationpage.view.shipments.shipToCustomer.dialog.');

    this.shipmentFetchConfig = {
      entityType: EntityPropertyTypes.SHIPMENT_BUSINESS_ID,
      filter: [FetchEntitiesFilters.ACTIVE, FetchEntitiesFilters.ON_LOCATION],
      action: this.props.actions.fetchEntities,
    };

    this.state = {
      sensorsDummyKey: {},
      stopExternalShipment: true,
    };
  }

  componentDidMount() {
    let locations = this.getLocations();

    if (locations.length === 1) {
      this.props.change('outgoingLocation', locations[0]);
    }

    if (PermissionManager.isTruckSensorsIntegrationEnabled()) {
      const dialogData = this.props.sData.get('dialogData');
      const defaultSensorProvider = dialogData?.sensorProviders?.find((sensorProvider) => sensorProvider.data.defaultProvider);
      const initialValues = {
        sensors: [{}],
        sensorProvider: defaultSensorProvider,
        sensorIntegration: dialogData?.sensorProviders?.length > 0,
      };

      if (locations.length === 1) {
        initialValues.outgoingLocation =  locations[0];
      }

      this.props.initialize(initialValues);

      if (defaultSensorProvider) {
        const shipmentId = this.props.sData.get('preselectedAssets').get(0)?.id;
        this.props.actions.getShipmentSensors(shipmentId, defaultSensorProvider?.data?.id, this.getSensorIntegrationErrorDialogBuilder());
      }
    }
  }

  getSensorIntegrationErrorDialogBuilder = () => {
    return (message, fnCloseDialog, retryCallback) => {
      let title = this.sensorIntegrationErrorLabels.get('title');

      const retryButtonAction = () => {
        fnCloseDialog();
        retryCallback();
      };

      const noSensorIntegrationButtonAction = () => {
        this.props.change('sensorIntegration', false);
        this.props.change('sensorProvider', null);
        this.props.change('sensors', [{}]);

        const shipmentId = this.props.sData.get('preselectedAssets').get(0)?.id;
        this.props.actions.stopShipment(shipmentId);
        fnCloseDialog();
      };

      const closeDialogButtonAction = () => {
        fnCloseDialog();
        this.onHide();
      };

      let buttons = [
        { id: 'close', text: this.dialogLabels.get('cancel'), action: closeDialogButtonAction },
        { id: 'retry', text: this.dialogLabels.get('retry'), action: retryButtonAction, bsStyle: 'primary', style: { marginRight: '20px' } },
        {
          id: 'noSensorIntegration',
          text: this.sensorIntegrationErrorLabels.get('btn.noSensorIntegration'),
          action: noSensorIntegrationButtonAction,
          bsStyle: 'primary',
        },
      ];

      let children = <MessageDialog.MessageArea text={message} />;
      return { title, children, buttons, type: 'error' };
    };
  };

  allSensorsConnected = () => {
    return this.props.sensors.every((sensor) => sensor.isConnected);
  };

  getDialogButtons() {
    return {
      left: [
        {
          id: 'cancel',
          text: this.dialogLabels.get('cancel'),
          action: this.onHide,
        },
      ],
      right: [
        {
          id: 'submit',
          text: this.dialogLabels.get('confirm'),
          bsStyle: 'primary',
          loading: this.props.sData.get('loading'),
          action: this.props.handleSubmit(this.onSubmit),
          disabled:
            this.props.sData.get('loading') ||
            this.props.sData.get('hasError') ||
            (this.props.sData.get('showIgnoreValidationCheckbox') && !this.props.sData.get('isIgnoreValidationWarnings')) ||
            (this.props.sensorIntegration && !this.allSensorsConnected()),
        },
      ],
    };
  }

  getShipmentId = (data) => {
    return data?.['add-asset-selection']?.assetsValue?.value || null;
  };

  getLocationId = (data) => {
    return data?.outgoingLocation?.value || null;
  };

  onSubmit = (data) => {
    let submitData = {
      shipmentId: this.getShipmentId(data),
      locationId: this.getLocationId(data),
      ignoreValidationWarnings: !PermissionManager.getOrgPreferences().asnShipmentLotAttachmentsRequired
        ? true
        : this.props.sData.get('showIgnoreValidationCheckbox') && data.footerValidationCheckbox
        ? data.footerValidationCheckbox
        : false,
    };

    this.setState({ stopExternalShipment: false }, () => {
      this.props.actions.submit(submitData, /*this.getMessageDialogBuilder()*/ null, this.props.reloadParentComponent);
    })

  };

  onHide = (onUnmount) => {
    if(onUnmount) return;

    if (this.props.sensorIntegration && this.state.stopExternalShipment) {
      const shipmentId = this.props.sData.get('preselectedAssets').get(0)?.id;
      this.props.actions.stopShipment(shipmentId);
    }

    this.props.actions.hide();
  };

  getFooterValidationCheckBox = () => {
    return this.props.sData.get('showIgnoreValidationCheckbox')
      ? { label: this.dialogLabels.get('ignorewarnings'), onChange: this.onFooterValidationChange }
      : null;
  };

  onFooterValidationChange = (value) => {
    this.props.actions.toggleIgnoreValidationWarningsCheckbox(value);
  };

  getLocations = () => {
    let dialogData = this.props.sData.get('dialogData');
    return dialogData?.locations || [];
  };

  updateSensorsDummyKey = () => {
    this.setState({ sensorsDummyKey: {} });
  };

  getSensorsListItem = (itemProps) => {
    return (
      <SensorsListItem
        {...itemProps}
        sensorProvider={this.props.sensorProvider}
        sensorIntegration={this.props.sensorIntegration}
        onSensorChange={this.updateSensorsDummyKey}
        actions={this.props.actions}
        sData={this.props.sData}
        change={this.props.change}
        sensorIntegrationErrorDialogBuilder={this.getSensorIntegrationErrorDialogBuilder}
      />
    );
  };

  getSensorProviderOptions = () => {
    let dialogData = this.props.sData.get('dialogData');
    return dialogData?.sensorProviders || [];
  };

  onSensorRemove = ({ sensor, isConnected }) => {
    if (!sensor || !isConnected) return;

    const shipmentId = this.props.sData.get('preselectedAssets').get(0)?.id;
    const sensorId = sensor.value;
    this.props.actions.unAssignSensorFromShipment(shipmentId, sensorId);
  };

  onSensorProviderChanged = ({ value }) => {
    if (!value) return;

    const shipmentId = this.props.sData.get('preselectedAssets').get(0)?.id;
    this.props.actions.getShipmentSensors(shipmentId, value, this.getSensorIntegrationErrorDialogBuilder());
    this.props.change('sensors', [{}]);
  };

  onSensorIntegrationChanged = (sensorIntegrationEnabled) => {
    this.props.change('sensors', [{}]);
    const shipmentId = this.props.sData.get('preselectedAssets').get(0)?.id;
    const sensorProviderId = this.props.sensorProvider?.value;

    if (sensorIntegrationEnabled && sensorProviderId) {
      sensorProviderId && this.props.actions.getShipmentSensors(shipmentId, sensorProviderId, this.getSensorIntegrationErrorDialogBuilder());
      return;
    }

    this.props.actions.stopShipment(shipmentId);
  };

  renderTrucksSensorsIntegration = () => {
    if (!PermissionManager.isTruckSensorsIntegrationEnabled()) {
      return null;
    }

    return (
      <div className="sensor-integration-container">
        <InputSection htmlFor="sensorIntegration" className="inline no-margin">
          <Checkbox
            name="sensorIntegration"
            id="sensorIntegration"
            label={this.labels.get('trucksSensorsIntegration')}
            onChangeCallback={this.onSensorIntegrationChanged}
          />
        </InputSection>

        <InputSection label={this.labels.get('sensorProvider')} htmlFor="sensorProvider" className="inline">
          <Combobox
            id="sensorProvider"
            name="sensorProvider"
            options={this.getSensorProviderOptions()}
            onChangeCallback={this.onSensorProviderChanged}
            validate={this.props.sensorIntegration && Validation.required}
            isDisabled={!this.props.sensorIntegration}
          />
        </InputSection>

        <div className="sensor-integration-section input-section inline">
          <AddRemoveListExt
            {...this.props}
            name="sensors"
            className="sensors-list"
            itemRendererComponent={this.getSensorsListItem}
            updateKey={this.state.sensorsDummyKey}
            hideAddAtButton={true}
            minFieldsCount={1}
            addButtonDisabled={!this.props.sensorIntegration}
            itemRendererOptions={this.props.sData.get('dialogData')?.sensors || []}
            addButtonLabel={this.labels.get('addSensor')}
            onRemoveCallback={this.onSensorRemove}
            maxItemsToOverflow={7}
          />
        </div>
      </div>
    );
  };

  render() {
    let preselectedAssets = this.props.sData.get('preselectedAssets');

    return (
      <Dialog
        id="ship-to-customer-dialog"
        className="ship-to-customer-dialog"
        show={this.props.sData.get('show')}
        onHide={this.onHide}
        sData={this.props.sData}
        titleText={this.labels.get('header.title')}
        footerValidationCheckbox={this.getFooterValidationCheckBox()}
        footerInformationIcon={dialogHelper.getFooterInformationIcon(this.props.sData, this.dialogLabels)}
        footerButtons={this.getDialogButtons()}
        onEnterKeyPress={this.props.handleSubmit(this.onSubmit)}
      >
        <div className="add-to-asset-container">
          <div className="add-to-asset-section input-section no-margin">
            <label className="add-to-asset-label">{this.labels.get('add')}</label>

            <EntitiesMultiSelect
              id="add-asset-selection"
              name="add-asset-selection"
              className="add-to-asset-selection"
              entitiesTypes={[getEnumValue(enumTypes.OBJECT_TYPE)('SHIPMENT')]}
              validate={Validation.required}
              preSelectedEntities={preselectedAssets}
              fetchConfig={this.shipmentFetchConfig}
              autoFocus={true}
              isMulti={false}
              disableOnPreselect={true}
              {...this.props}
            />
          </div>

          <label className="add-to-asset-arrow">
            <i className="pl pl-move-arrow-right" />
          </label>

          <div className="add-to-asset-section input-section no-margin">
            <label className="add-to-asset-label">{this.labels.get('to')}</label>
            <Combobox id="outgoingLocation" name="outgoingLocation" options={this.getLocations()} validate={Validation.required} />
          </div>
        </div>

        {this.renderTrucksSensorsIntegration()}
      </Dialog>
    );
  }
}

const _ShipToCustomerDialog = reduxForm({
  form: 'shipToCustomerDialog',
  onChange: (values, dispatch, props, previousValues) => {
    dialogHelper.onFormChangeHandler(values, props, previousValues);
  },
})(ShipToCustomerDialog);

const selector = formValueSelector('shipToCustomerDialog');

const mapStateToProps = (state) => {
  return {
    sensorIntegration: selector(state, 'sensorIntegration'),
    sensorProvider: selector(state, 'sensorProvider'),
    sensors: selector(state, 'sensors'),
  };
};

export default connect(mapStateToProps)(_ShipToCustomerDialog);

ShipToCustomerDialog.propTypes = {
  actions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
};
